import React from "react";
import PropTypes from "prop-types";

import ApplicantsExportModal from "../ApplicantsExportModal";
import AllocateRequestModal from "./AllocateRequestModal";
import ApplicantTagModal from "./ApplicantTagModal";
import ApproveRequestModal from "./ApproveRequestModal";
import ChangeRoleModal from "./ChangeRoleModal";
import DeleteTagConfirmModal from "./DeleteTagConfirmModal";
import ReactivateRequestModal from "./ReactivateRequestModal";
import EditRemovalReasonModal from "./EditRemovalReasonModal";
import RejectRequestModal from "./RejectRequestModal";
import RemoveRequestModal from "./RemoveRequestModal";
import UnassignRequestModal from "./UnassignRequestModal";

const ApplicantsModals = ({
  applicantsExport,
  applicantsTab,
  approveApplicants,
  approveModalVariant,
  allocateCopy,
  allocateZoneOptions,
  closeAllocateModal,
  closeApproveModal,
  closeChangeRoleModal,
  closeApplicantTag,
  closeDeleteTag,
  closeRejectModal,
  closeRemoveModal,
  closeReactivateModal,
  closeEditReasonModal,
  closeUnassignModal,
  confirmApprove,
  confirmChangeRole,
  confirmDeleteTag,
  confirmReject,
  confirmReactivate,
  confirmEditReason,
  deleteTagTargetIds,
  eventRoleOptions,
  eventShiftOptions,
  isAllocateModalOpen,
  isApproveModalOpen,
  isChangeRoleModalOpen,
  isRejectModalOpen,
  isRemoveModalOpen,
  isReactivateModalOpen,
  isEditReasonModalOpen,
  isUnassignModalOpen,
  isBulkTag,
  tagOptions,
  portalVariant,
  myCompanyId,
  onNavigateToZones,
  rejectApplicants,
  reactivateApplicants,
  editReasonApplicants,
  reactivateCopy,
  editRemovalReasonCopy,
  changeRoleApplicants,
  changeRoleCopy,
  removeCopy,
  selectedApplicant,
  selectedApplicants,
  sharedLabels,
  unassignCopy,
  onAllocateConfirm,
  onRemoveConfirm,
  onSaveTag,
  onUnassignConfirm,
}) => (
  <>
    <ApplicantTagModal
      open={Boolean(selectedApplicant)}
      userTag={selectedApplicant?.userTag}
      isBulk={isBulkTag}
      selectedApplicants={selectedApplicants}
      tagOptions={tagOptions}
      portalVariant={portalVariant}
      myCompanyId={myCompanyId}
      onClose={closeApplicantTag}
      onSave={onSaveTag}
    />
    <ApproveRequestModal
      open={isApproveModalOpen}
      variant={approveModalVariant}
      applicants={approveApplicants}
      roleOptions={eventRoleOptions}
      shiftOptions={eventShiftOptions}
      onClose={closeApproveModal}
      onApprove={confirmApprove}
    />
    <RejectRequestModal
      open={isRejectModalOpen}
      applicants={rejectApplicants}
      copy={applicantsTab.joiningRequests.rejectModal}
      onClose={closeRejectModal}
      onReject={confirmReject}
    />
    <ChangeRoleModal
      open={isChangeRoleModalOpen}
      applicants={changeRoleApplicants}
      zoneOptions={allocateZoneOptions}
      roleOptions={eventRoleOptions}
      shiftOptions={eventShiftOptions}
      copy={changeRoleCopy}
      onClose={closeChangeRoleModal}
      onConfirm={confirmChangeRole}
    />
    <DeleteTagConfirmModal
      open={deleteTagTargetIds.length > 0}
      selectedApplicants={selectedApplicants}
      portalVariant={portalVariant}
      myCompanyId={myCompanyId}
      onClose={closeDeleteTag}
      onConfirm={confirmDeleteTag}
    />
    <AllocateRequestModal
      open={isAllocateModalOpen}
      applicants={selectedApplicants}
      zoneOptions={allocateZoneOptions}
      copy={allocateCopy}
      onClose={closeAllocateModal}
      onApprove={onAllocateConfirm}
      onZonesTabClick={() => {
        closeAllocateModal();
        onNavigateToZones?.();
      }}
    />
    <UnassignRequestModal
      open={isUnassignModalOpen}
      applicants={selectedApplicants}
      zoneOptions={allocateZoneOptions}
      copy={unassignCopy}
      onClose={closeUnassignModal}
      onUnassign={onUnassignConfirm}
    />
    <RemoveRequestModal
      open={isRemoveModalOpen}
      applicants={selectedApplicants}
      zoneOptions={allocateZoneOptions}
      copy={removeCopy}
      onClose={closeRemoveModal}
      onRemove={onRemoveConfirm}
    />
    <ReactivateRequestModal
      open={isReactivateModalOpen}
      applicants={reactivateApplicants}
      copy={reactivateCopy}
      onClose={closeReactivateModal}
      onConfirm={confirmReactivate}
    />
    <EditRemovalReasonModal
      open={isEditReasonModalOpen}
      applicants={editReasonApplicants}
      copy={editRemovalReasonCopy}
      onClose={closeEditReasonModal}
      onSave={confirmEditReason}
    />
    <ApplicantsExportModal
      open={applicantsExport.isOpen}
      title={applicantsTab.export}
      subtitle={applicantsExport.copy.subtitle}
      cancelLabel={sharedLabels.cancel}
      confirmLabel={applicantsExport.copy.confirmExport}
      fields={applicantsExport.fields}
      selectedFields={applicantsExport.selectedFields}
      isAllSelected={applicantsExport.isAllSelected}
      confirmDisabled={applicantsExport.isConfirmDisabled}
      exportDate={applicantsExport.exportDate}
      dateError={applicantsExport.dateError}
      onClose={applicantsExport.closeModal}
      onConfirm={applicantsExport.submit}
      onFieldChange={applicantsExport.handleFieldChange}
      onSelectAll={applicantsExport.handleSelectAll}
      onDateChange={applicantsExport.handleDateChange}
    />
  </>
);

ApplicantsModals.propTypes = {
  applicantsExport: PropTypes.object.isRequired,
  applicantsTab: PropTypes.object.isRequired,
  approveApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  approveModalVariant: PropTypes.string.isRequired,
  allocateCopy: PropTypes.object.isRequired,
  allocateZoneOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  closeAllocateModal: PropTypes.func.isRequired,
  closeApproveModal: PropTypes.func.isRequired,
  closeChangeRoleModal: PropTypes.func.isRequired,
  closeApplicantTag: PropTypes.func.isRequired,
  closeDeleteTag: PropTypes.func.isRequired,
  closeRejectModal: PropTypes.func.isRequired,
  closeRemoveModal: PropTypes.func.isRequired,
  closeReactivateModal: PropTypes.func.isRequired,
  closeEditReasonModal: PropTypes.func.isRequired,
  closeUnassignModal: PropTypes.func.isRequired,
  confirmApprove: PropTypes.func.isRequired,
  confirmChangeRole: PropTypes.func.isRequired,
  confirmDeleteTag: PropTypes.func.isRequired,
  confirmReject: PropTypes.func.isRequired,
  confirmReactivate: PropTypes.func.isRequired,
  confirmEditReason: PropTypes.func.isRequired,
  deleteTagTargetIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  eventRoleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  eventShiftOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  isAllocateModalOpen: PropTypes.bool.isRequired,
  isApproveModalOpen: PropTypes.bool.isRequired,
  isChangeRoleModalOpen: PropTypes.bool.isRequired,
  isRejectModalOpen: PropTypes.bool.isRequired,
  isRemoveModalOpen: PropTypes.bool.isRequired,
  isReactivateModalOpen: PropTypes.bool.isRequired,
  isEditReasonModalOpen: PropTypes.bool.isRequired,
  isUnassignModalOpen: PropTypes.bool.isRequired,
  isBulkTag: PropTypes.bool,
  tagOptions: PropTypes.arrayOf(PropTypes.object),
  portalVariant: PropTypes.string,
  myCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onNavigateToZones: PropTypes.func,
  rejectApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  reactivateApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  editReasonApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  reactivateCopy: PropTypes.object.isRequired,
  editRemovalReasonCopy: PropTypes.object.isRequired,
  changeRoleApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  changeRoleCopy: PropTypes.object.isRequired,
  removeCopy: PropTypes.object.isRequired,
  selectedApplicant: PropTypes.object,
  selectedApplicants: PropTypes.arrayOf(PropTypes.object).isRequired,
  sharedLabels: PropTypes.object.isRequired,
  unassignCopy: PropTypes.object.isRequired,
  onAllocateConfirm: PropTypes.func.isRequired,
  onRemoveConfirm: PropTypes.func.isRequired,
  onSaveTag: PropTypes.func.isRequired,
  onUnassignConfirm: PropTypes.func.isRequired,
};

ApplicantsModals.defaultProps = {
  onNavigateToZones: undefined,
  selectedApplicant: null,
  isBulkTag: false,
  tagOptions: [],
  portalVariant: "company-portal",
  myCompanyId: undefined,
};

export default ApplicantsModals;
